import * as Sentry from '@sentry/react';
import unfetch from 'unfetch';

import APIError from 'api/utils/APIError';
import { ErrorResponse } from 'api/utils/ErrorResponse';

type UnfetchResponse = Awaited<ReturnType<typeof unfetch>>;

interface FetchOptions {
  headers?: Record<string, string>;
  body?: object;
  /** URL query params (`?foo=bar`) */
  params?: Record<string, string | number | undefined | boolean>;
  /**
   * Always send `testindex=true` on dev/staging, without the page URL opting in via
   * `?testIndex=true`. Ignored in production so prod requests never read the test index.
   */
  useTestIndex?: boolean;
}

const deployEnv = import.meta.env.VITE_APP_DEPLOY_ENV;
const isDevOrStaging = deployEnv === 'development' || deployEnv === 'staging';
const hasTestIndexParam =
  isDevOrStaging && new URLSearchParams(window.location.search).get('testIndex') === 'true';

function shouldAppendTestIndex(apiUrl: string, useTestIndex?: boolean): boolean {
  if (!isDevOrStaging || !apiUrl.includes('turn4api')) return false;
  return hasTestIndexParam || !!useTestIndex;
}

function buildUrl(url: string, options?: FetchOptions): string {
  const params = { ...options?.params };

  if (shouldAppendTestIndex(url, options?.useTestIndex)) {
    // Lowercase on purpose: the backend's lookup is case-sensitive and ignores `testIndex`.
    params.testindex = true;
  }

  const entries = Object.entries(params).filter(([, val]) => val !== undefined);
  if (entries.length === 0) return url;

  const paramString = entries.map(([key, value]) => `${key}=${value}`).join('&');
  return `${url}?${paramString}`;
}

async function request(
  method: string,
  url: string,
  options?: FetchOptions,
): Promise<UnfetchResponse> {
  const fullURL = buildUrl(url, options);

  if (options?.body instanceof FormData) {
    return unfetch(fullURL, {
      method,
      headers: {
        appId: 'Turn4',
        ...options?.headers,
      },
      body: options?.body ? options.body : undefined,
    });
  }

  return unfetch(fullURL, {
    method,
    headers: {
      appId: 'Turn4',
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    body: options?.body ? JSON.stringify({ ...options.body }) : undefined,
  });
}

async function throwIfNotOk(res: UnfetchResponse, fullURL: string): Promise<void> {
  if (res.ok) return;

  let errorData: ErrorResponse | undefined;
  try {
    errorData = await res.json();
  } catch (e) {
    Sentry.captureException(e, { extra: { url: fullURL, status: res.status } });
  }

  if (errorData) {
    throw new APIError(errorData.message, res.status, errorData.subCode, errorData.requestId);
  }

  throw new APIError(res.statusText || 'An unexpected error occurred', res.status);
}

/**
 * Make a POST request
 */
export async function post<T>(url: string, options?: FetchOptions): Promise<T> {
  return fetchJson<T>('POST', url, options);
}

/**
 * POST, parse JSON, and return body plus response headers (same idea as reading `response.headers` then `response.json()`).
 */
export async function postWithHeaders<T>(
  url: string,
  options?: FetchOptions,
): Promise<{ data: T; headers: UnfetchResponse['headers'] }> {
  const res = await request('POST', url, options);
  await throwIfNotOk(res, url);
  const data = (await res.json()) as T;
  return { data, headers: res.headers };
}

/**
 * GET, parse JSON, and return body plus response headers.
 */
export async function getWithHeaders<T>(
  url: string,
  options?: FetchOptions,
): Promise<{ data: T; headers: UnfetchResponse['headers'] }> {
  const res = await request('GET', url, options);
  await throwIfNotOk(res, url);
  const data = (await res.json()) as T;
  return { data, headers: res.headers };
}

/**
 * Make a PATCH request
 */
export async function patch<T>(url: string, options?: FetchOptions): Promise<T> {
  return fetchJson<T>('PATCH', url, options);
}

/**
 * Make a GET request
 */
export async function get<T>(url: string, options?: FetchOptions): Promise<T> {
  return fetchJson<T>('GET', url, options);
}

/**
 * Make a PUT request
 */
export async function put<T>(url: string, options?: FetchOptions): Promise<T> {
  return fetchJson<T>('PUT', url, options);
}

export async function fetch<T>(method: string, url: string, options?: FetchOptions): Promise<T> {
  return fetchJson<T>(method, url, options);
}

async function fetchJson<T>(method: string, url: string, options?: FetchOptions): Promise<T> {
  const res = await request(method, url, options);
  await throwIfNotOk(res, url);
  return res.json() as Promise<T>;
}
